import is from '@sindresorhus/is';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { LearningOpportunity } from '~/shared/data-contract/learning-opportunities';
import { learningOpportunityListSchema } from '~/shared/data-contract/learning-opportunities';

type LearningRecsPathname = `/users/${string}/profile-skill-jobs/${string}/learning-opportunities`;

export const useLearningOpportunityRecommendationsQuery = (
    userId: string,
    jobId: string | undefined,
    isEnabled: boolean,
    limit?: number,
): UseQueryResult<readonly LearningOpportunity[], Error> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: LearningRecsPathname = `/users/${userId}/profile-skill-jobs/${jobId}/learning-opportunities`;
    const { applicationState } = useContext(ApplicationContext);
    const { language } = applicationState;
    const requestOptions = is.number(limit)
        ? {
              headers: {
                  'Accept-Language': language.code,
              },
              searchParams: new URLSearchParams({ limit: limit.toString() }).toString(),
          }
        : undefined;

    return useQuery({
        enabled: is.nonEmptyString(userId) && is.string(jobId) && isEnabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(learningOpportunityListSchema, {
                pathname,
                requestOptions,
            });
            if (result.isOk) {
                return result.value;
            }
            throw result.error;
        },
        queryKey: [pathname, requestOptions, learningOpportunityListSchema],
    });
};

export const useUserFeaturedLearningOpportunitiesQuery = (
    userId: string,
    enabled = true,
): UseQueryResult<readonly LearningOpportunity[], Error> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: `/${string}` = `/users/${userId}/featured-opportunities`;

    return useQuery({
        enabled: is.nonEmptyString(userId) && enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(learningOpportunityListSchema, {
                pathname,
            });
            if (result.isOk) {
                return result.value;
            }
            throw result.error;
        },
        queryKey: [pathname, learningOpportunityListSchema],
    });
};
